import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {IconButton, Button} from '@primer/react'
import {useCallback, useImperativeHandle, useState} from 'react'
import {ConversationCommentBox} from '@github-ui/diff-lines/conversation-comment-box'
import {getItem, setItem} from '@github-ui/safe-storage/session-storage'
import {IDS} from '@github-ui/commenting/DomElements'
import {clsx} from 'clsx'
import styles from '../CommentsSidePanel.module.css'
import {useCopilotQuotaGate} from '../use-copilot-quota-gate'

const collapseStateLocalStorageKey = 'pullRequestCommentsPanelFooterCollapseState'

export interface CommentsSidePanelFooterHandle {
  expand: () => boolean
}

export interface CommentsSidePanelFooterProps {
  commentText: string
  onCommentTextChange: (value: string) => void
  onSubmitComment: () => void
  isPending: boolean
  ref?: React.Ref<CommentsSidePanelFooterHandle>
}

export function CommentsSidePanelFooter({
  commentText,
  onCommentTextChange,
  onSubmitComment,
  isPending,
  ref,
}: CommentsSidePanelFooterProps) {
  // TODO: if this becomes pinnable, we will have to come up with an SSR safe strategy that
  // avoids CLS. Might have to be a setting we store on the server and return in the payload
  const defaultCollapseState = getItem(collapseStateLocalStorageKey)
  const [isMinimized, setIsMinimized] = useState(defaultCollapseState === 'true')
  const {banner: quotaBanner, submitDisabled: quotaSubmitDisabled} = useCopilotQuotaGate(commentText)

  const handleSubmitComment = useCallback(() => {
    if (quotaSubmitDisabled) return
    onSubmitComment()
  }, [quotaSubmitDisabled, onSubmitComment])

  useImperativeHandle(
    ref,
    () => ({
      expand() {
        const wasMinimized = isMinimized
        setIsMinimized(false)
        setItem(collapseStateLocalStorageKey, 'false')
        return wasMinimized
      },
    }),
    [isMinimized],
  )

  return (
    <div className={clsx(styles.SidePanelFooter, 'd-flex flex-column gap-2 width-full')} id={IDS.issueCommentComposer}>
      <h4 className="d-flex">
        <IconButton
          aria-label={isMinimized ? 'Open PR comment panel' : 'Close PR comment panel'}
          icon={isMinimized ? ChevronRightIcon : ChevronDownIcon}
          size="small"
          aria-expanded={!isMinimized}
          tooltipDirection="n"
          variant="invisible"
          className="mr-2"
          onClick={() => {
            const newState = !isMinimized
            setIsMinimized(newState)
            setItem(collapseStateLocalStorageKey, String(newState))
          }}
        />
        <span className="pb-2">Add a comment</span>
      </h4>
      {!isMinimized && (
        <>
          {quotaBanner}
          <ConversationCommentBox
            label="Add a comment"
            value={commentText}
            onChange={onCommentTextChange}
            onPrimaryAction={handleSubmitComment}
            disabled={isPending}
            maxHeightLines={15}
          >
            <Button
              variant="primary"
              onClick={handleSubmitComment}
              inactive={isPending || commentText.trim().length === 0 || quotaSubmitDisabled}
            >
              Comment
            </Button>
          </ConversationCommentBox>
        </>
      )}
    </div>
  )
}
